import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Home, ChevronRight, Search, Plus, Edit2, Trash2, ShieldCheck, Save } from 'lucide-react';

const blankForm = { name: '', address: '', is_active: true };

const CustodianList = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);          // null => create
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  }), []);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const res = await fetch(`${backendUrl}/api/custodians?${params}`, { headers: authHeaders() });
      if (res.ok) setItems(await res.json());
    } finally { setLoading(false); }
  }, [backendUrl, search, authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditing(null); setForm(blankForm); setError(''); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name || '', address: row.address || '', is_active: row.is_active });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.address.trim()) {
      setError('Custodian Name and Address are required.');
      return;
    }
    try {
      setSaving(true); setError('');
      const url = editing
        ? `${backendUrl}/api/custodians/${editing.id}`
        : `${backendUrl}/api/custodians`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Save failed');
      }
      setDialogOpen(false);
      await fetchAll();
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Deactivate custodian "${row.name}"?`)) return;
    const res = await fetch(`${backendUrl}/api/custodians/${row.id}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    if (res.ok) await fetchAll();
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-muted">
        <Home className="w-4 h-4" />
        <ChevronRight className="w-3 h-3" />
        <span>Settings</span>
        <ChevronRight className="w-3 h-3" />
        <span className="text-text-dark font-semibold">Custodian Master</span>
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-8 h-8 text-rose-600" />
          <div>
            <h1 className="text-2xl font-bold font-manrope" data-testid="custodian-heading">Custodian Master</h1>
            <p className="text-sm text-text-muted">Manage custodians used as the "To" party on Lifting Advices and related documents.</p>
          </div>
        </div>
        <Button onClick={openCreate} className="bg-rose-600 hover:bg-rose-700" data-testid="add-custodian-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Custodian
        </Button>
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="custodian-search-input"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-text-muted">
                <th className="py-2 px-3 font-semibold">Custodian Name</th>
                <th className="py-2 px-3 font-semibold">Address</th>
                <th className="py-2 px-3 font-semibold">Status</th>
                <th className="py-2 px-3 font-semibold w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="py-6 text-center text-text-muted">Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={4} className="py-6 text-center text-text-muted">No custodians yet. Click "Add Custodian" to create one.</td></tr>
              ) : items.map((c) => (
                <tr key={c.id} className="border-b hover:bg-gray-50" data-testid={`custodian-row-${c.id}`}>
                  <td className="py-2 px-3 font-medium">{c.name}</td>
                  <td className="py-2 px-3 whitespace-pre-line text-text-muted">{c.address}</td>
                  <td className="py-2 px-3">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${c.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-custodian-${c.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(c)} data-testid={`delete-custodian-${c.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Custodian' : 'Add Custodian'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium mb-1">
                Custodian Name <span className="text-rose-600">*</span>
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. THE MANAGER, CSC/GSEC"
                data-testid="custodian-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Address <span className="text-rose-600">*</span>
              </label>
              <Textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows={6}
                placeholder="Full address (multi-line allowed)"
                data-testid="custodian-address-input"
              />
            </div>
            {error && (
              <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded p-2">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="save-custodian-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustodianList;
